<template>
  <ValidationObserver
    ref="dropdownObserver" >
    <ValidationProvider
      v-slot="{ errors }"
      :name="schema.title"
      :rules="validators">
      <multiselect
        :id="dropdownId"
        :ref="dropdownId"
        v-model="modelData"
        :placeholder="schema && schema.placeholder ? schema.placeholder : 'Select'"
        class="flex-options hide-tags no-selected-background"
        :options="schema.options"
        label="label"
        track-by="label"
        :multiple="false"
        :show-labels="false"
        :show-no-results="false"
        :close-on-select="true"
        :clear-on-select="false"
        :preserve-search="true"
        :allow-empty="!required"
        :preselect-first="required"
        :searchable="false" >
        <template
          slot="selection"
          slot-scope="{ values }">
          <span
            v-if="values.length"
            class="multiselect__single" >{{ values }}</span>
        </template>
        <template
          slot="singleLabel"
          slot-scope="value">
          <span
            v-if="true"
            class="multiselect__single"
            :class="{ 'image': value.option.image }"
            :style="{ backgroundImage: `url(${value.option.image})` }" >{{ value.option.label }}</span>
        </template>
        <template
          slot="option"
          slot-scope="props">
          <span
            class="greedy"
            :class="{ 'image': props.option.image }"
            :style="{ backgroundImage: `url(${props.option.image})` }" >{{ props.option.label }}</span>
          <SBIcon
            :icon="SBIconList.DONE_CIRCLE"
            :color="SBColors.bluePrimary"
            :size="13"/>
        </template>
      </multiselect>
      <div
        v-if="schema.subtitle && !errors[0]"
        class="subtitle flex flex-align-center">
        <svg
          v-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string' && schema.subtitlePrefixIcon.endsWith('.svg')"
          class="mr-1 flex-start-image"
          :data-src="schema.subtitlePrefixIcon"
          fill="currentColor"
          width="10"
          height="10" />
        <img
          v-else-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string'"
          :src="schema.subtitlePrefixIcon"
          style="width: 10px; height: 10px;"
          class="mr-1 flex-start-image"
        >
        <SBIcon
          v-else-if="!schema.noSubtitlePrefixIcon"
          class="mr-1"
          :icon="SBIconList.INFO"
          :color="SBColors.greyLight"
          :size="10" />
        <span>{{ subtitle }}</span>
      </div>
      <div
        v-if="errors[0]"
        class="error flex flex-align-center">
        <SBIcon
          class="mr-1"
          :icon="SBIconList.ERROR"
          :color="SBColors.redPrimary"
          :size="10" />
        <span>{{ errors[0] }}</span>
      </div>
    </ValidationProvider>
  </ValidationObserver>
</template>

<script>
import Multiselect from 'vue-multiselect';

import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import randomString from '@/helpers/randomString';

export default {
  name: 'MultiselectWrapper',
  components: {
    Multiselect,
    SBIcon,
  },
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    validators: {
      type: String,
      default: '',
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      modelData: null,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    dropdownId() {
      return `dropdown-${randomString()}`;
    },
    // NOTE: This is a computed property for placeholder text
    subtitle() {
      const modifierMatch = this.schema.subtitle.match(/\{\{ option:(.*)? \}\}/, 'g');

      let replaceValue = this.modelData ? this.modelData.label : (
        this.schema.subtitlePlaceholderDefault || `selected ${this.schema.title.toLowerCase()}`
      );
      let re = new RegExp(/\{\{\s*option\s*\}\}/, 'g');
      if (modifierMatch && modifierMatch[1]) {
        re = new RegExp(`{{\\s*option:${modifierMatch[1]}\\s*}}`, 'g');
        replaceValue = modifierMatch[1] === 'lowercase' ? replaceValue.toLowerCase() : (modifierMatch[1] === 'uppercase' ? replaceValue.toUpperCase() : replaceValue);
        switch (modifierMatch[1]) {
          case 'lowercase':
            replaceValue.toLowerCase();
            break;
          case 'uppercase':
            replaceValue.toUpperCase();
            break;
          case 'capitalize':
            replaceValue = `${replaceValue.charAt(0).toUpperCase()}${replaceValue.slice(1)}`;
            break;
          default:
            break;
        }
      }
      return this.schema.subtitle.replaceAll(re, replaceValue);
    },
  },
  watch: {
    modelData: {
      handler() {
        this.emitData();
      },
      immediate: true,
    },
  },
  mounted() {
    this.setValue();
    if (this.schema.stepID) {
      this.$eventBus.$on(`validate-${this.schema.stepID}`, () => {
        if (!this.$refs.dropdownObserver) return;
        this.$refs.dropdownObserver.validate();
      });
    }
    setTimeout(() => {
      this.emitData(true);
      if (this.schema.value) {
        if (!this.$refs.dropdownObserver) return;
        this.$refs.dropdownObserver.validate();
      }
    }, 0);
  },
  methods: {
    randomString,
    emitData(reset = false) {
      // console.log(1);
      if (!this.$refs.dropdownObserver) return;
      // console.log(2);
      this.$refs.dropdownObserver.validate({ silent: true })
        .then((valid) => {
          // console.log(3, valid);
          this.$emit('input', {
            valid,
            name: this.schema.returnKey || '',
            data: {
              value: {},
              ...this.modelData,
            },
          });
        })
        .finally(() => {
          if (reset) this.$refs.dropdownObserver.reset();
        });
    },
    setValue() {
      if (this.schema.value) {
        let found = false;
        // In case matchOptionKey is provided try to match it with the value
        if (this.schema.matchOptionKey) {
          const targetOption = this.schema.options.find(option => option.value && option.value[this.schema.matchOptionKey] === this.schema.value);
          if (targetOption) {
            this.modelData = targetOption;
            found = true;
          }
        }
        // In case the value is an object, try to find the object in the options
        if (!found) {
          const targetOption = this.schema.options.find(option => JSON.stringify(option.value) === JSON.stringify(this.schema.value));
          if (targetOption) {
            this.modelData = targetOption;
            found = true;
          }
        }
        // if the modelData is not found, try to find it by sorting the keys in the object
        if (!found) {
          const targetOption = this.schema.options.find(option => this.$lodash.isEqual(this.sortObjectByKeys(option.value), this.sortObjectByKeys(this.schema.value)));
          if (targetOption) {
            this.modelData = targetOption;
            found = true;
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .flex-options {
  .multiselect__select, .multiselect__tags {
    height: 34px !important;
  }
}

.flex-start-image {
  top: 1px;
  position: relative;
}
// .multiselect__content-wrapper {
//   position: fixed !important;
// }

// .multiselect__single {
//   padding: 50px 20px;
// }

// .multiselect__option .image,
// .multiselect__single .image {
//   padding-left: 22px;
//   background-repeat: no-repeat;
//   background-size: 14px 14px;
//   background-position: center left;
// }

::v-deep {
  .multiselect__option .image,
  .multiselect__single .image {
    padding-left: 22px;
    background-repeat: no-repeat;
    background-size: 14px 14px;
    background-position: center left;
  }
}
</style>
